import { logger } from '@/src/core/utils';
import env from './env';

/**
 * RevenueCat Web Billing configuration
 *
 * Uses @revenuecat/purchases-js for web subscriptions (powered by Stripe internally).
 * The purchase flow opens an inline checkout overlay on the page.
 * After a successful purchase, the revenuecat-webhook Supabase Edge Function
 * receives the Stripe event and updates the `subscriptions` table with platform='web'.
 */

let PurchasesWebSDK: any = null;

function getWebSDK(): any {
  if (PurchasesWebSDK !== null) return PurchasesWebSDK;
  if (!env.revenuecatStripeKey) return null;
  try {
    PurchasesWebSDK = require('@revenuecat/purchases-js').Purchases;
    return PurchasesWebSDK;
  } catch {
    logger.debug('RevenueCatWeb', 'SDK not available');
    PurchasesWebSDK = false; // Avoid repeated require attempts
    return null;
  }
}

function getSharedInstance(): any {
  const SDK = getWebSDK();
  if (!SDK) return null;
  try {
    return SDK.getSharedInstance();
  } catch {
    return null;
  }
}

export async function configureRevenueCatWeb(userId?: string): Promise<void> {
  if (!userId) return;
  const SDK = getWebSDK();
  if (!SDK) return;
  try {
    SDK.configure({ apiKey: env.revenuecatStripeKey!, appUserId: userId });
    logger.info('RevenueCatWeb', 'Configured successfully', { userId });
  } catch (error) {
    logger.warn('RevenueCatWeb', 'Failed to configure', { error });
  }
}

export async function loginRevenueCatWeb(userId: string): Promise<void> {
  const SDK = getWebSDK();
  if (!SDK || !env.revenuecatStripeKey) return;
  try {
    // Re-configure with new userId (changeUser requires an existing instance)
    SDK.configure({ apiKey: env.revenuecatStripeKey, appUserId: userId });
    logger.debug('RevenueCatWeb', 'User logged in', { userId });
  } catch (error) {
    logger.warn('RevenueCatWeb', 'Failed to log in user', { error });
  }
}

export async function checkEntitlementsWeb(): Promise<boolean> {
  const instance = getSharedInstance();
  if (!instance) return false;
  try {
    const customerInfo = await instance.getCustomerInfo();
    const isPro = customerInfo.entitlements.active['pro'] !== undefined;
    logger.debug('RevenueCatWeb', 'Entitlement check', { isPro });
    return isPro;
  } catch (error) {
    logger.warn('RevenueCatWeb', 'Failed to check entitlements', { error });
    return false;
  }
}

export async function getOfferingsWeb(): Promise<any> {
  const instance = getSharedInstance();
  if (!instance) return null;
  try {
    return await instance.getOfferings();
  } catch (error) {
    logger.warn('RevenueCatWeb', 'Failed to get offerings', { error });
    return null;
  }
}

export async function purchasePackageWeb(pkg: any): Promise<boolean> {
  const instance = getSharedInstance();
  if (!instance) return false;
  try {
    const result = await instance.purchase({ rcPackage: pkg });
    return result.customerInfo.entitlements.active['pro'] !== undefined;
  } catch (error: any) {
    // ErrorCode.UserCancelledError = 1
    if (error?.errorCode === 1) {
      logger.debug('RevenueCatWeb', 'User cancelled purchase');
      return false;
    }
    logger.error('RevenueCatWeb', 'Purchase failed', { error });
    throw error;
  }
}
